import React, { useState } from 'react';

import { RowButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { ModalDialogType } from '@eika/modal';

import { Kid } from '../../generated-types';
import useNoteMaxLength from '../../hooks/useNoteMaxLength';
import useTranslation from '../../hooks/useTranslation';
import { NoteTypes } from '../../translations/translationType';
import { EikaModal } from '../EikaModal';
import Note from './Note';
import NoteFieldLayout from './NoteFieldLayout';

import './NoteField.scss';

type SelectedReceiver = import('../transaction/SelectedReceiver').SelectedReceiver;

interface Props {
    labelText?: string;
    placeholder?: string;
    selectedReceiver?: SelectedReceiver;
    note?: string;
    kid?: string;
    ownText?: string;
    ownTextLabel?: string;
    error?: string;
    onChange?: (value: string) => void;
    onChangeKid?: (kid: Kid | undefined) => void;
    onChangeOwnText?: (value: string) => void;
    onShow?: (show: boolean) => void;
    locked?: boolean;
    disabled?: boolean;
    forceDisableKid?: boolean;
    animateModalAs?: ModalDialogType;
    labelAlwaysOn?: boolean;
    icon?: IconTypes;
    noteMaxLength?: number;
}

export const getNoteType = (selectedReceiver?: SelectedReceiver, disableKid = false, isKidOnly = false): NoteTypes => {
    if (disableKid) return NoteTypes.MESSAGE;
    if (selectedReceiver?.kidRequired || isKidOnly) return NoteTypes.KID;
    if (!selectedReceiver) return NoteTypes.KID_OR_MESSAGE;
    if (!selectedReceiver?.kidFormats?.length) return NoteTypes.MESSAGE;
    return NoteTypes.KID_OR_MESSAGE;
};

const NoteField: React.FC<Props> = ({
    labelText,
    placeholder,
    selectedReceiver,
    note,
    kid,
    ownText,
    ownTextLabel,
    error,
    onChange,
    onChangeKid,
    onShow,
    locked = false,
    disabled = false,
    forceDisableKid = false,
    onChangeOwnText,
    animateModalAs,
    labelAlwaysOn,
    icon = IconTypes.LITEN_PIL_HOEYRE,
    noteMaxLength,
}) => {
    const translated = useTranslation();
    const [showModal, setShowModal] = useState(false);

    const triggerModal = (show: boolean) => {
        setShowModal(show);
        onShow?.(show);
    };

    const noteType = getNoteType(selectedReceiver, forceDisableKid);
    const noteLabel = labelText ?? translated.amountNote[noteType].label;
    const notePlaceholder = placeholder ?? translated.amountNote[noteType].placeholder;

    const ownTextMaxLength = useNoteMaxLength();
    const showToggleKidMessage = noteType === NoteTypes.KID_OR_MESSAGE;

    const getNoteText = () => {
        switch (noteType) {
            case NoteTypes.KID:
                return kid;
            case NoteTypes.MESSAGE:
                return note;
            default:
                return kid || note;
        }
    };

    const noteText = getNoteText();

    return (
        <>
            <RowButton
                onClick={() => triggerModal(true)}
                disabled={disabled || locked}
                className="note-field__row-btn"
                data-testid="note-field"
            >
                <NoteFieldLayout
                    placeholder={locked ? '' : notePlaceholder}
                    icon={locked ? <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" /> : icon}
                    label={noteLabel}
                    note={noteText}
                    error={error}
                    labelAlwaysOn={labelAlwaysOn || (locked && !noteText)}
                />
            </RowButton>
            <EikaModal id="noteField" open={showModal} setOpen={triggerModal} animateAs={animateModalAs}>
                <Note
                    showToggleKidMessage={showToggleKidMessage}
                    noteLabel={labelText}
                    note={note}
                    kid={kid}
                    ownText={ownText}
                    ownTextLabel={ownTextLabel}
                    toAccount={selectedReceiver}
                    onChange={(value) => {
                        triggerModal(false);
                        onChange?.(value);
                    }}
                    onChangeKid={(value) => {
                        triggerModal(false);
                        onChangeKid?.(value);
                    }}
                    onChangeOwnText={
                        onChangeOwnText
                            ? (value) => {
                                  triggerModal(false);
                                  onChangeOwnText?.(value);
                              }
                            : undefined
                    }
                    placeholder={placeholder ?? undefined}
                    ownTextMaxLength={ownTextMaxLength}
                    noteMaxLength={noteMaxLength}
                    type={noteType}
                />
            </EikaModal>
        </>
    );
};

export default NoteField;
